import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from 'utils';
import { RootState } from 'data/root.reducers';
import * as api from './networkId.api';
import { getSelectedNetworkId } from './networkId.selectors';
import { SYSTEM_CONFIGURATION_ROUTE, NETWORK_ID_ROUTE, NETWORK } from '../../constants';
import {
  GET_NETWORK_ID_LIST,
  GET_NETWORK_ID,
  CREATE_NETWORK_ID,
  DELETE_NETWORK_ID,
  GO_TO_NETWORK_ID_LIST,
  GO_TO_NETWORK_ID_VIEW,
  GO_TO_NETWORK_ID_CREATE,
  SELECT_NETWORK_ID,
  SET_NETWORK_ID_LOADING,
  DeleteNetworkIdThunkAction,
  CreateNetworkIdThunkAction,
  SetNetworkIdLoadingAction,
  NetworkIdActionTypes,
  NetworkId,
  SelectNetworkIdAction,
} from './networkId.types';

export function goToNetworkIdList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_NETWORK_ID_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${NETWORK_ID_ROUTE}`);
  };
}

export function goToNetworkIdCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_NETWORK_ID_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${NETWORK_ID_ROUTE}/create`);
  };
}

export function goToNetworkIdView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_NETWORK_ID_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${NETWORK_ID_ROUTE}/view`);
  };
}

export function setNetworkIdLoadingAction(
  isLoading: boolean,
): SetNetworkIdLoadingAction {
  return {
    type: SET_NETWORK_ID_LOADING,
    payload: { isLoading },
  };
}

export function getNetworkIdList(deliveryFunctionId: string): NetworkIdActionTypes {
  return {
    type: GET_NETWORK_ID_LIST,
    payload: api.getNetworkIdList(deliveryFunctionId),
  };
}

export function getNetworkId(
  deliveryFunctionId: string, id: string,
): NetworkIdActionTypes {
  return {
    type: GET_NETWORK_ID,
    payload: api.getNetworkId(deliveryFunctionId, id),
  };
}

export function createNetworkId(
  deliveryFunctionId: string,
  body: WithAuditDetails<NetworkId>,
  history: History,
  shouldShowSuccessMessageAndRedirect = true,
): CreateNetworkIdThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_NETWORK_ID,
        payload: api.createNetworkId(deliveryFunctionId, body),
      });

      dispatch(getNetworkIdList(deliveryFunctionId));
      if (shouldShowSuccessMessageAndRedirect) {
        dispatch(goToNetworkIdList(history));
        showSuccessMessage(i18n.t('networkId.create.success', { networkId: body.data?.networkId }), 5000);
      }

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('networkId.create.failed', { networkId: body.data?.networkId, error: errorMessage }));
    }
  };
}

export function createNetworkIdFromForm(
  deliveryFunctionId: string,
  body: WithAuditDetails<NetworkId>,
): CreateNetworkIdThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setNetworkIdLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_NETWORK_ID,
        payload: api.createNetworkId(deliveryFunctionId, body),
      });

      showSuccessMessage(i18n.t('networkId.create.success', { networkId: body.data?.networkId }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('networkId.create.failed', { networkId: body.data?.networkId, error: errorMessage }));

    } finally {
      dispatch(setNetworkIdLoadingAction(false));
      dispatch(getNetworkIdList(deliveryFunctionId));
    }
  };
}

export function deleteNetworkId(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
  networkId?: string,
): DeleteNetworkIdThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedNetworkId] = getSelectedNetworkId(getState());

    try {
      await dispatch({
        type: DELETE_NETWORK_ID,
        payload: api.deleteNetworkId(deliveryFunctionId, id, body),
      });
      showSuccessMessage(i18n.t('networkId.delete.success', { networkId }), 5000);
      if (history != null) {
        dispatch(goToNetworkIdList(history));
      }
    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      const fallbackMessage = i18n.t('networkId.delete.failed', { networkId });
      showError(errorMessage ? `${fallbackMessage} ${errorMessage}` : fallbackMessage);

    } finally {
      dispatch(getNetworkIdList(deliveryFunctionId));
    }
  };
}

export function selectNetworkId(
  networkId: NetworkId[],
): SelectNetworkIdAction {
  return {
    type: SELECT_NETWORK_ID,
    payload: networkId,
  };
}
